import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  Heart, Wind, ThermometerSun, Brain, Moon, Coffee, Pill,
  Activity, TrendingUp, TrendingDown, Minus, Calendar,
  RefreshCw, Info, CheckCircle, AlertTriangle, Phone,
  MessageSquare, Clock, User, FileText
} from 'lucide-react';

// Symptom configuration
const SYMPTOMS = [
  { key: 'pain', label: 'Pain & Comfort', icon: Heart, color: '#ef4444', description: 'Physical pain level' },
  { key: 'dyspnea', label: 'Breathing', icon: Wind, color: '#f97316', description: 'Shortness of breath' },
  { key: 'nausea', label: 'Nausea', icon: ThermometerSun, color: '#eab308', description: 'Stomach discomfort' },
  { key: 'anxiety', label: 'Anxiety', icon: Brain, color: '#8b5cf6', description: 'Worry or nervousness' },
  { key: 'drowsiness', label: 'Energy', icon: Coffee, color: '#14b8a6', description: 'Alertness and fatigue' }
];

// Family-friendly score labels
const getScoreLabel = (score) => {
  if (score <= 2) return { text: 'Well Controlled', color: 'text-green-600 bg-green-100' };
  if (score <= 4) return { text: 'Mild', color: 'text-yellow-600 bg-yellow-100' };
  if (score <= 6) return { text: 'Moderate', color: 'text-orange-600 bg-orange-100' };
  return { text: 'Needs Attention', color: 'text-red-600 bg-red-100' };
};

const getTrendIcon = (trend) => {
  if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-red-500" />;
  if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-green-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
};

// Symptom Card Component
const SymptomCard = ({ symptom, score, trend, lastUpdated }) => {
  const Icon = symptom.icon;
  const scoreInfo = getScoreLabel(score);
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${symptom.color}20` }}>
              <Icon className="w-5 h-5" style={{ color: symptom.color }} />
            </div>
            <div>
              <h4 className="font-medium">{symptom.label}</h4>
              <p className="text-xs text-muted-foreground">{symptom.description}</p>
            </div>
          </div>
          {getTrendIcon(trend)}
        </div>
        
        <div className="flex items-center justify-between">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${scoreInfo.color}`}>
            {scoreInfo.text}
          </span>
          {lastUpdated && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(lastUpdated).toLocaleDateString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Trend Chart Component
const SymptomTrendChart = ({ data, selectedSymptoms }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Symptom trend data will appear here</p>
        </div>
      </div>
    );
  }

  // Transform data for family-friendly display (invert scale so lower is better)
  const transformedData = data.map(point => {
    const newPoint = { date: point.date };
    SYMPTOMS.forEach(s => {
      if (selectedSymptoms.includes(s.key) && point[s.key] !== undefined) {
        // Keep original for tooltip but visual shows comfort level
        newPoint[s.key] = point[s.key];
      }
    });
    return newPoint;
  });

  return (
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={transformedData}>
        <defs>
          {SYMPTOMS.filter(s => selectedSymptoms.includes(s.key)).map(symptom => (
            <linearGradient key={symptom.key} id={`gradient-${symptom.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={symptom.color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={symptom.color} stopOpacity={0}/>
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="date" 
          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          stroke="#6b7280"
          fontSize={12}
        />
        <YAxis 
          domain={[0, 9]} 
          ticks={[0, 3, 6, 9]} 
          stroke="#6b7280"
          fontSize={12}
          tickFormatter={(val) => {
            if (val <= 2) return 'Good';
            if (val <= 5) return 'Mild';
            return 'High';
          }}
        />
        <Tooltip 
          labelFormatter={(date) => new Date(date).toLocaleDateString()}
          formatter={(value, name) => {
            const symptom = SYMPTOMS.find(s => s.key === name);
            const label = getScoreLabel(value);
            return [label.text, symptom?.label || name];
          }}
          contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}
        />
        <Legend 
          formatter={(value) => {
            const symptom = SYMPTOMS.find(s => s.key === value);
            return symptom?.label || value;
          }}
        />
        {SYMPTOMS.filter(s => selectedSymptoms.includes(s.key)).map(symptom => (
          <Area
            key={symptom.key}
            type="monotone"
            dataKey={symptom.key}
            stroke={symptom.color}
            fill={`url(#gradient-${symptom.key})`}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Main Family Symptom Portal Component
export default function FamilySymptomPortal() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [patientInfo, setPatientInfo] = useState(null);
  const [currentSymptoms, setCurrentSymptoms] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [selectedSymptoms, setSelectedSymptoms] = useState(['pain', 'dyspnea', 'anxiety']);
  const [careTeam, setCareTeam] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get patient linked to family member
      const patientId = user?.linked_patient_id || 'P001'; // Default for demo
      
      // Load patient info
      const patientRes = await api.get(`/ehr/patients/${patientId}`).catch(() => null);
      
      // Load latest HOPE observation
      const latestRes = await api.get(`/hope/patient/${patientId}/latest`).catch(() => null);
      
      // Load trends
      const trendsRes = await api.post('/hope/trends', {
        patient_id: patientId,
        days: 14
      }).catch(() => null);
      
      // Process patient info
      if (patientRes?.data) {
        setPatientInfo(patientRes.data);
      } else {
        setPatientInfo({
          name: 'Your Loved One',
          diagnosis: 'Under hospice care',
          physician: 'Dr. Johnson',
          nurse: 'Sarah RN'
        });
      }
      
      // Process symptoms
      if (latestRes?.data?.observation) {
        const obs = latestRes.data.observation;
        const symptoms = {};
        for (const sym of obs.symptoms || []) {
          symptoms[sym.symptom_type] = {
            score: sym.impact_score,
            trend: sym.change_from_baseline > 0 ? 'increasing' : 
                   sym.change_from_baseline < 0 ? 'decreasing' : 'stable'
          };
        }
        setCurrentSymptoms(symptoms);
        setLastUpdate(obs.visit_timestamp);
      } else {
        // Mock data for demo
        setCurrentSymptoms({
          pain: { score: 3, trend: 'stable' },
          dyspnea: { score: 2, trend: 'decreasing' },
          nausea: { score: 1, trend: 'stable' },
          anxiety: { score: 4, trend: 'stable' },
          drowsiness: { score: 3, trend: 'stable' }
        });
        setLastUpdate(new Date().toISOString());
      }
      
      // Process trends
      if (trendsRes?.data?.trends) {
        setTrendData(trendsRes.data.trends);
      } else {
        // Mock trend data
        const mockTrends = [];
        for (let i = 13; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          mockTrends.push({
            date: date.toISOString(),
            pain: Math.max(1, Math.min(6, 3 + Math.floor(Math.random() * 3) - 1)),
            dyspnea: Math.max(1, Math.min(5, 2 + Math.floor(Math.random() * 2))),
            anxiety: Math.max(2, Math.min(6, 4 + Math.floor(Math.random() * 2) - 1))
          });
        }
        setTrendData(mockTrends);
      }
      
      // Mock care team
      setCareTeam([
        { name: 'Dr. Sarah Johnson', role: 'Physician', phone: '(555) 123-4567' },
        { name: 'Maria Garcia, RN', role: 'Nurse Case Manager', phone: '(555) 234-5678' },
        { name: 'James Wilson, MSW', role: 'Social Worker', phone: '(555) 345-6789' },
        { name: 'Rev. Emily Brown', role: 'Chaplain', phone: '(555) 456-7890' }
      ]);
      
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white" data-testid="family-symptom-portal">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {patientInfo?.name}'s Care Status
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Last updated: {lastUpdate ? new Date(lastUpdate).toLocaleString() : 'Recently'}
              </p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Information Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900">
                  <strong>Understanding This Information:</strong> These symptom scores help you understand how your loved one is feeling. 
                  Lower scores mean symptoms are well-controlled. The care team monitors these closely and adjusts care as needed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Status Overview */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Current Symptom Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SYMPTOMS.map(symptom => (
              <SymptomCard
                key={symptom.key}
                symptom={symptom}
                score={currentSymptoms[symptom.key]?.score || 0}
                trend={currentSymptoms[symptom.key]?.trend || 'stable'}
                lastUpdated={lastUpdate}
              />
            ))}
          </div>
        </div>

        {/* Overall Status Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Overall Care Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800 font-medium">Well Controlled</p>
                <p className="text-2xl font-bold text-green-700">
                  {Object.values(currentSymptoms).filter(s => s.score <= 3).length}
                </p>
                <p className="text-xs text-green-600">symptoms</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">Being Monitored</p>
                <p className="text-2xl font-bold text-yellow-700">
                  {Object.values(currentSymptoms).filter(s => s.score > 3 && s.score <= 6).length}
                </p>
                <p className="text-xs text-yellow-600">symptoms</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">Next Visit</p>
                <p className="text-lg font-bold text-blue-700">Tomorrow</p>
                <p className="text-xs text-blue-600">10:00 AM - RN Visit</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trend Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>2-Week Symptom Trends</CardTitle>
                <CardDescription>See how symptoms have changed over time</CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                {SYMPTOMS.slice(0, 3).map(symptom => (
                  <Button
                    key={symptom.key}
                    size="sm"
                    variant={selectedSymptoms.includes(symptom.key) ? "default" : "outline"}
                    onClick={() => {
                      setSelectedSymptoms(prev => 
                        prev.includes(symptom.key) 
                          ? prev.filter(s => s !== symptom.key)
                          : [...prev, symptom.key]
                      );
                    }}
                    style={selectedSymptoms.includes(symptom.key) ? { backgroundColor: symptom.color } : {}}
                  >
                    {symptom.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <SymptomTrendChart data={trendData} selectedSymptoms={selectedSymptoms} />
            
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-2">What do these trends mean?</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>Stable or decreasing</strong> trends show good symptom control</li>
                <li>• <strong>Slight increases</strong> are normal and the care team is aware</li>
                <li>• <strong>Significant changes</strong> prompt the team to adjust the care plan</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Care Team */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Your Care Team
            </CardTitle>
            <CardDescription>Contact any team member with questions or concerns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {careTeam.map((member, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{member.name}</p>
                    <p className="text-sm text-muted-foreground">{member.role}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Phone className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline">
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">For Urgent Concerns</p>
                  <p className="text-sm text-amber-800">
                    Call the 24/7 nurse line: <strong>703-578-7108</strong>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resources */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Helpful Resources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2">
                <Heart className="w-6 h-6 text-red-500" />
                <span>Pain Management Guide</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2">
                <Wind className="w-6 h-6 text-blue-500" />
                <span>Breathing Comfort Tips</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2">
                <Brain className="w-6 h-6 text-purple-500" />
                <span>Caregiver Support</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
